/**
 * MSW Override: Booking engine error scenario
 *
 * Rejects one booking call with a CORE-SUBSCRIPTION-BOOKING-ENGINE-* platform code so every treatment can be
 * checked locally before the backend ships. One endpoint per error funnel:
 *
 *   confirm      payment page            -> both modals
 *   draft        select / customize      -> useErrorNotification
 *   quotation    picking a flight        -> useTripQuotation
 *   availability confirming a selection  -> useTripConfirmation
 *
 * Switch scenario from the devtools console, no restart:
 *
 *   localStorage.setItem('msw.bookingEngineError.code', 'CORE-SUBSCRIPTION-BOOKING-ENGINE-SESSION-EXPIRED')
 *   localStorage.setItem('msw.bookingEngineError.endpoint', 'quotation') // 'confirm' is the default
 *
 * Enable by setting ENABLE_MSW=true and NUXT_PUBLIC_MSW_OVERRIDES_BOOKING_ENGINE_ERROR=true in
 * your .env, then restart the dev server.
 */

import type { HttpHandler } from 'msw';
import { http, HttpResponse, bypass } from 'msw';
import { BFF_ENDPOINTS, toMSWPattern } from '@cvo/domain';

type FailingEndpoint = 'confirm' | 'draft' | 'quotation' | 'availability';

const UNPROCESSABLE_STATUS = 422;
const SERVER_ERROR_STATUS = 500;

function readSetting(key: string, fallback: string): string {
  return globalThis.localStorage?.getItem(`msw.bookingEngineError.${key}`) ?? fallback;
}

function bookingEngineErrorResponse(code: string) {
  return HttpResponse.json(
    {
      code,
      message: 'The booking engine rejected this booking.',
      source: 'workflow-orchestrator',
      details: [
        { name: 'providerErrorCode', reason: 'nsk-server:MockedProviderCode' },
        { name: 'providerErrorMessage', reason: `MSW scenario for ${code}` },
      ],
    },
    { status: code === 'CORE-SUBSCRIPTION-BOOKING-ENGINE-UNAVAILABLE' ? SERVER_ERROR_STATUS : UNPROCESSABLE_STATUS },
  );
}

export function createBookingEngineErrorOverrides(baseUrl: string): HttpHandler[] {
  const sanitizedBaseUrl = baseUrl.replace(/\/$/, '');

  function rejectWhenSelected(endpoint: FailingEndpoint) {
    return async ({ request }: { request: Request }) => {
      if (readSetting('endpoint', 'confirm') !== endpoint) {
        const originalResponse = await fetch(bypass(request));
        return HttpResponse.json(await originalResponse.json(), { status: originalResponse.status });
      }

      const code = readSetting('code', 'CORE-SUBSCRIPTION-BOOKING-ENGINE-REJECTED');
      console.log(`[MSW] booking engine error: rejecting ${endpoint} with ${code}`);
      return bookingEngineErrorResponse(code);
    };
  }

  return [
    // The only booking payment call.
    http.post(`${sanitizedBaseUrl}/${toMSWPattern(BFF_ENDPOINTS.BOOKING_PAYMENTS)}`, rejectWhenSelected('confirm')),
    http.post(`${sanitizedBaseUrl}/${toMSWPattern(BFF_ENDPOINTS.CREATE_BOOKING)}`, rejectWhenSelected('draft')),
    http.post(`${sanitizedBaseUrl}/${toMSWPattern(BFF_ENDPOINTS.QUOTATION)}`, rejectWhenSelected('quotation')),
    // POST only: the availability confirmation. The browse GET shares this path and is left alone.
    http.post(`${sanitizedBaseUrl}/${toMSWPattern(BFF_ENDPOINTS.TRIP_SEARCH)}`, rejectWhenSelected('availability')),
  ];
}
